// FAQ.jsx
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Bot, Target, Wallet, CreditCard, GraduationCap, ShoppingCart, Dumbbell, CircleCheck, Gift } from "lucide-react";
import "./FAQ.css";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import { trackAppStoreClick, trackGooglePlayClick } from "../analytics/googleAnalytics";
import { PRECIO_ANUAL } from "../config/precios";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    // Va la primera a propósito: es la objeción número uno de quien se plantea
    // pagar ("¿para qué, si le pregunto a ChatGPT gratis?"). Además, esta
    // pregunta la gente se la hace literalmente a ChatGPT y a Perplexity, y
    // esos buscadores construyen la respuesta leyendo páginas como esta.
    {
      question: "¿En qué se diferencia de ChatGPT?",
      answer: "En que ChatGPT no sabe lo que has comido hoy, y Wayo sí. Escaneas el plato con una foto y Wayo ya conoce tus calorías, tus macros y tu objetivo. Cuando le preguntas qué cenar, te contesta con tus números: \"te quedan 430 kcal y te faltan 42 g de proteína, cena salmón con brócoli\". A ChatGPT tendrías que contárselo todo a mano, cada vez.",
      Icon: Bot
    },
    {
      question: "¿Los planes son personalizados?",
      answer: "Sí, te daremos consejos en función de tus objetivos.",
      Icon: Target
    },
    // Los números salen del código, no del marketing. Fuente de verdad:
    //   comidas -> Fitverse/config/escaneos.php  (3/día, 60/mes, 7 días de gracia)
    //   asistente -> Fitverse/config/asistente.php  (3 preguntas de por vida)
    // Si tocas esos límites, actualiza esta respuesta. Ya pasó una vez: el
    // 20-ago-2026 la cuota subió de 2/20 a 3/60 y esto se quedó anunciando la
    // vieja, o sea prometiendo MENOS de lo que la app da.
    {
      question: "¿Es gratis?",
      answer: `Sí, y puedes usarla gratis siempre. Registras hasta 3 comidas al día (60 al mes), con foto o a mano, que da para cerrar el día entero: desayuno, comida y cena. Tu primera semana además va sin ningún límite. Y tienes 3 preguntas para el asistente Wayo. Premium quita todos los límites y añade el plan de nutrición y entreno con IA, la lista de la compra y las estadísticas completas: ${PRECIO_ANUAL} al año, con los primeros 7 días gratis.`,
      Icon: Wallet
    },
    // Los 7 días gratis piden tarjeta (es la oferta introductoria de la tienda
    // vía RevenueCat, y Stripe en web). Decirlo aquí quita el miedo antes de
    // descargar y evita que se sientan engañados al llegar al paywall.
    {
      question: "¿Los 7 días gratis me piden tarjeta?",
      answer: "Sí. La prueba se activa desde la App Store o Google Play, así que te piden un método de pago. No se te cobra nada si cancelas antes de que acaben los 7 días, y puedes cancelar desde el móvil en dos toques.",
      Icon: CreditCard
    },
    {
      question: "¿Necesito saber de nutrición?",
      answer: "Para nada. Wayo te lo explica todo en lenguaje simple y te guía paso a paso sin términos complicados.",
      Icon: GraduationCap
    },
    {
      question: "¿La IA me recomienda qué comer?",
      answer: "Sí, la app te sugiere comidas y listas de compra personalizadas basadas en tus objetivos nutricionales.",
      Icon: ShoppingCart
    },
    {
      question: "¿Necesito saber de entrenamiento?",
      answer: "No, dentro de la app encontrarás diferentes ejercicios en función de tu experiencia.",
      Icon: Dumbbell
    },
    {
      question: "¿Puedo cancelar cuando quiera?",
      answer: "Claro. Sin compromisos. Si pruebas el plan premium, puedes cancelar en cualquier momento.",
      Icon: CircleCheck
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Schema FAQPage derivado del MISMO array que se renderiza, para que el
  // structured data siempre coincida con lo visible (requisito de Google).
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <section id="faq" className="faq-section faq-home">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <div className="faq-container">
        
        {/* Header */}
        <div className="faq-header">
          <span className="faq-badge">FAQ</span>
          <h2 className="faq-title">
            Preguntas <span className="title-highlight">frecuentes</span>
          </h2>
          <p className="faq-description">
            Todo lo que necesitas saber sobre Wayo. ¿No encuentras tu respuesta? 
            <a href="mailto:soporte@wayoai.app" className="faq-contact"> Contáctanos</a>
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="faq-grid">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${openIndex === index ? 'active' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <button 
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span className="faq-icon">
                  <faq.Icon size={22} strokeWidth={2} aria-hidden="true" />
                </span>
                <span className="faq-question-text">{faq.question}</span>
                <span className={`faq-toggle ${openIndex === index ? 'open' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path 
                      d="M5 7.5L10 12.5L15 7.5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <div className={`faq-answer ${openIndex === index ? 'show' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="faq-cta">
          <h3 className="faq-cta-title">¿Listo para empezar?</h3>
          <p className="faq-cta-text">Descarga Wayo ahora y comienza tu viaje hacia una alimentación más saludable.</p>
          <div className="hero-trial-badge" style={{ marginBottom: '20px' }}>
            <strong><Gift size={16} strokeWidth={2.5} aria-hidden="true" />7 días Premium gratis</strong><br />Escanea sin límites desde el primer día
          </div>
          <div className="store-badges" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
            <a
              href="https://apps.apple.com/app/id6756500478"
              className="store-badge app-store"
              onClick={() => trackAppStoreClick("faq")}
            >
              <img src={appStoreImg} alt="App Store" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
              className="store-badge google-play"
              onClick={() => trackGooglePlayClick("faq")}
            >
              <img src={googlePlayImg} alt="Google Play" />
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
